import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Button,
  Input,
  message,
  Typography,
  Breadcrumb,
  TableProps,
  Col,
  Divider,
  Modal,
  Row,
  Space,
} from "antd";
import { useNavigate } from "react-router-dom";

import {
  DeleteSuperUsers,
  fetchEmpDetails,
  fetchSuperUsersData,
  SaveSuperUsers,
} from "../../../core/services/account-request.service";
import { SuperUser } from "../../../core/interfaces/SuperUser";
import "./SuperAdminSetup.css";
import { SearchOutlined } from "@ant-design/icons";
import { Employee } from "../../../core/interfaces/Employee";
import { User } from "../../../core/interfaces/User";
import { CalClient } from "@cvx/cal";
import { UserContext } from "../../../context/user-context";
import { environment } from "../../../environments/environment";
import { appInsights } from "../../../core/services/insight.service";
import GlobalErrorMessage from "../../global-error/GlobalErrorMessage";

const { Title } = Typography;

const SuperAdminSetup: React.FC = () => {
  const [adminUsers, setAdminUsers] = useState<SuperUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = React.useState<SuperUser>(
    {} as SuperUser
  );
  const [deleteResult, setDeleteResult] = React.useState<void>();
  const [open, setOpen] = useState(false);
  const [empData, setEmpData] = useState<Employee[]>([]);
  const cal = new CalClient(environment.calConfig);
  const [graphUser, setGraphUser] = useState<User>({});
  const user = useContext(UserContext);
  const [result, setResult] = React.useState<string | undefined>();
  const [apiError, setApiError] = useState(null);
  const userProfile = useContext(UserContext);
  const getUserEmail = async () => {
    try {
      const response = await cal.getUserPropertiesFromMsGraph([
        "userPrincipalName",
      ]);
      setGraphUser((prev) => ({ ...prev, ...user }));
      setGraphUser((prev) => ({ ...prev, Emaild: response.userPrincipalName }));
      appInsights.trackEvent({
        name: "Get User Email Success",
        properties: { userId: user.email },
      });
    } catch (error) {
      setApiError(error);
      message.error("Failed to get user email");
      appInsights.trackException({
        exception: error as Error,
        properties: {
          Component: "SuperAdminSetup",
          method: "getUserEmail",
          context: "Get User Email",
          userId: user.email,
        },
      }); // Track exception
    }
  };
  useEffect(() => {
    fetchSuperUsers();
    getUserEmail();
  }, []);

  const fetchSuperUsers = async () => {
    setLoading(true);
    try {
      const response = await fetchSuperUsersData(userProfile); // Adjust the API endpoint as needed
      setAdminUsers(response);
      appInsights.trackEvent({
        name: "Fetch Super Users Success",
        properties: { userId: user.email },
      });
    } catch (error) {
      setApiError(error);
      message.error("Failed to fetch super users");
      appInsights.trackException({
        exception: error as Error,
        properties: {
          Component: "SuperAdminSetup",
          method: "fetchSuperUsers",
          context: "Fetch Super Users",
          userId: user.email,
        },
      }); // Track exception
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedUser(null);
  };

  const handleSave = async () => {
    if (!selectedUser) {
      message.error("User Email ID is Required");
      return;
    }
    const emailExists = adminUsers.some((user) => user.emailID === selectedUser);
    if (emailExists) {
      message.error("This email already exists in the list of super admin users.");
      return;
    }
    const response = await fetchEmpDetails(selectedUser);
    setResult(response.length > 0 ? response[0].fullName : undefined);
    const data: SuperUser = {
      cai: selectedUser,
      name: response.length > 0 ? response[0].fullName : "",
      sponsor: graphUser.email || "",
      emailID: selectedUser,
      objectID: selectedUser,
    };
    try {
      const response = await SaveSuperUsers(data,userProfile);
      setSearchResults(response);
      setLoading(true);
      message.success("User saved successfully");
      fetchSuperUsers();
      setSelectedUser(null);
      appInsights.trackEvent({
        name: "Save Super Users Success",
        properties: { userId: user.email, selectedUser: selectedUser },
      });
    } catch (error) {
      setApiError(error);
      message.error("Failed to save user");
      appInsights.trackException({
        exception: error as Error,
        properties: {
          Component: "SuperAdminSetup",
          method: "handleSave",
          context: "Handle Save",
          userId: user.email,
          selectedUser: selectedUser,
        },
      }); // Track exception
    }
  };

  const handleRemove = async () => {
    if (!selectedUser) {
      message.error("User Email ID is Required");
      return;
    }
    const emailID = selectedUser;
    try {
      const response = await DeleteSuperUsers(emailID,userProfile);
      setDeleteResult(response);
      setLoading(true);
      message.success("User deleted successfully");
      fetchSuperUsers();
      setSelectedUser(null);
      appInsights.trackEvent({
        name: "Delete Super Users Success",
        properties: { userId: user.email, selectedUser: selectedUser },
      });
    } catch (error) {
      setApiError(error);
      message.error("Failed to delete user");
      appInsights.trackException({
        exception: error as Error,
        properties: {
          Component: "SuperAdminSetup",
          method: "handleRemove",
          context: "Handle Remove",
          userId: user.email,
          selectedUser: selectedUser,
        },
      }); // Track exception
    }
  };

  const handleExit = () => {
    navigate("/home"); // Adjust the route as needed
  };
  const RedirectLink = async (e: any, record: SuperUser) => {
    e.preventDefault();
    const emailID = record.emailID;
    if (emailID) {
      setSelectedUser(emailID);
    }
  };
  const showModal = async (e: any) => {
    setSelectedUser(e);
    setOpen(true);
    if (e && e.trim() !== "") {
      await SearchUser(e);
    } else {
      setEmpData([]);
    }
  };
  const SearchUser = async (text: any) => {
    setLoading(true);
    try {
      const response = await fetchEmpDetails(text); // Adjust the API endpoint as needed
      setEmpData(response);
      appInsights.trackEvent({
        name: "Search User Success",
        properties: { userId: user.email, searchText: text },
      });
    } catch (error) {
      setApiError(error);
      message.error("Failed to fetch users");
      appInsights.trackException({
        exception: error as Error,
        properties: {
          Component: "SuperAdminSetup",
          method: "SearchUser",
          context: "Search User",
          userId: user.email,
          searchText: text,
        },
      }); // Track exception
    } finally {
      setLoading(false);
    }
  };
  const setRequester = (text: string) => {
    setSelectedUser(text);
    setOpen(false);
    setEmpData([]);
  };
  const handlePressEnter = (e: any) => {
    showModal(e.currentTarget.value);
  };
  function handleModalInputChange(e: string): void {
    setSelectedUser(e);
  }
  const handleOk = async (e: any) => {
    setOpen(false);
  };
  const handleCancel = async (e: any) => {
    setOpen(false);
  };
  const column: TableProps<Employee>["columns"] = [
    {
      title: "S/N",
      dataIndex: "index",
      key: "index",
      render: (text: string, record: Employee, index: number) =>
        `${index + 1}.`,
      width: "15%",
    },
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (text) => <a>{text}</a>,
      width: "20%",
    },
    {
      title: "User ID/Email ID",
      dataIndex: "emailAddress",
      key: "emailAddress",
      align: "center",
      width: "20%",
    },
    {
      title: "PhoneExt",
      dataIndex: "phoneExt",
      key: "phoneExt",
      width: "15%",
    },

    {
      title: "Department",
      dataIndex: "deptName",
      key: "deptName",
      width: "20%",
    },
    {
      title: "Location",
      dataIndex: "locationName",
      key: "locationName",
      width: "20%",
    },
  ];

  const columns = [
    {
      title: "S/N",
      dataIndex: "index",
      key: "index",
      render: (text: string, record: SuperUser, index: number) =>
        `${index + 1}.`,
    },
    {
      title: "Email ID",
      dataIndex: "emailID",
      key: "emailID",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: SuperUser) => (
        <a
          href={`/administration/super-admin-setup`}
          onClick={(event) => RedirectLink(event, record)}
        >
          {text}
        </a>
      ),
    },
  ];
  const handleRetry = () => {
    setApiError(null);
  };
  return (
    <>
      <Breadcrumb
        items={[
          {
            title: "administration",
          },
          {
            title: "super admin setup",
          },
        ]}
      />
       {apiError ? (
               <GlobalErrorMessage 
               error={apiError}
               visible={!!apiError}
               onClose={() => setApiError(null)}
               onRetry={handleRetry}
             />
             ) : (
      <div className="div-container">
        <Title level={3} style={{ textAlign: "left" }}>
          Admin Setup
        </Title>
        <Table
          dataSource={adminUsers}
          columns={columns}
          rowKey="email"
          loading={loading}
          bordered={true}
          rowClassName={() => "white-background-row"}
          style={{ marginTop: "30px" }}
          onRow={(record) => ({
            onClick: () => setSelectedUser(record.emailID),
          })}
        />
        <div style={{ marginTop: "20px" }}>
          <Input
            placeholder="User Email ID"
            value={selectedUser || ""}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{ width: "200px", marginRight: "10px" }}/>
            <a onClick={() => showModal(selectedUser)}>
            <SearchOutlined />
            </a>
          <Button onClick={handleReset} style={{ marginRight: "10px" }}>
            Reset
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            style={{ marginRight: "10px" }}
          >
            Save
          </Button>
          <Button danger onClick={handleRemove} style={{ marginRight: "10px" }}>
            Remove
          </Button>
          <Button onClick={handleExit}>Exit</Button>
        </div>
        <Modal
          title="Search For Employee"
          open={open}
          onOk={handleOk}
          onCancel={handleCancel}
          width={1000}
        >
          <Divider></Divider>
          <Space direction="vertical">
            <Row gutter={[16, 32]}>
              <Col span={16}>
                <div>Enter Search Query</div>
              </Col>
            </Row>
            <Row gutter={[16, 32]}>
              <Col span={18}>
                <div>
                  <Input
                    value={selectedUser || ""}
                    onChange={(e) => handleModalInputChange(e.target.value)}
                  />
                </div>
              </Col>
              <Col span={6}>
                <div>
                  <Button
                    type="primary"
                    onClick={() => SearchUser(selectedUser)}
                  >
                    Search
                  </Button>
                </div>
              </Col>
            </Row>
          </Space>
          <Table
            dataSource={empData}
            columns={column}
            pagination={false}
            scroll={{ y: 150 }}
            loading={loading}
            locale={{
              emptyText:
                "No matching record found. Kindly refine your criteria!",
            }}
            onRow={(record) => ({
              onClick: () => setRequester(record.emailAddress),
              onChange: () => setResult(record.fullName),
            })}
            style={{ marginTop: "20px" }}
            size="small"
          />
          <Divider></Divider>
        </Modal>
      </div>)}
    </>
  );
};

export default SuperAdminSetup;
